import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { generateReport } from '../../services/reportService';
import jsPDF from 'jspdf';
import {
    ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import logo from '../../logo.svg';

const ReportGenerator = ({ fieldId, userId }) => {
    const { t, language } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [statusText, setStatusText] = useState('');
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);
    const reportRef = useRef(null);

    // Stop loading when report data is received so the modal can show
    useEffect(() => {
        if (reportData) {
            setStatusText('Rapor Hazır. Önizleme açılıyor...');
            setLoading(false);
        }
    }, [reportData]);

    const handleGenerateReport = async () => {
        setLoading(true);
        setStatusText('Running AI Analysis... (This may take 15–20 seconds)');
        setError(null);
        setReportData(null);

        try {
            const response = await generateReport(fieldId, userId, language);

            if (!response.success) {
                throw new Error(response.message || 'Rapor oluşturulamadı');
            }

            setStatusText('Data received. Building report...');
            setReportData(response.data);

        } catch (err) {
            console.error('Error generating report:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Rapor oluşturulamadı';
            setError(errorMessage);
            setLoading(false);
        }
    };

    const generatePDF = async () => {
        if (!reportRef.current || !reportData) return;
        setStatusText('PDF Hazırlanıyor...');

        try {
            const doc = new jsPDF('p', 'mm', 'a4');

            // CLONE STRATEGY:
            // 1. Clone the report element
            // 2. Append to body with fixed width and high contrast styling
            // 3. Capture, then remove
            const originalElement = reportRef.current;
            const clone = originalElement.cloneNode(true);

            // Style the clone for perfect printing
            Object.assign(clone.style, {
                position: 'fixed',
                top: '0',
                left: '0',
                width: '800px', // Fixed A4-like width
                height: 'auto',
                zIndex: '-9999',
                background: 'white',
                padding: '40px',
                visibility: 'visible',
                overflow: 'visible' // Ensure no scrollbars
            });

            document.body.appendChild(clone);

            await doc.html(clone, {
                callback: function (pdf) {
                    // Clean up clone
                    document.body.removeChild(clone);

                    // Add Footer to each page
                    const totalPages = pdf.getNumberOfPages();
                    for (let i = 1; i <= totalPages; i++) {
                        pdf.setPage(i);
                        addFooter(pdf, i);
                    }

                    const fileName = `${reportData.fieldInfo.name.replace(/\s+/g, '_')}_Rapor_${new Date().toISOString().split('T')[0]}.pdf`;
                    pdf.save(fileName);
                    setStatusText('İndirme Başarılı!');
                    setTimeout(() => setStatusText(''), 2000);
                },
                x: 10,
                y: 10,
                width: 190,
                windowWidth: 800, // Match clone width
                margin: [10, 10, 10, 10],
                html2canvas: {
                    scale: 0.7, // Higher quality
                    useCORS: true,
                    logging: false,
                    windowWidth: 800
                }
            });

        } catch (err) {
            console.error('PDF Generation failed:', err);
            setError(`PDF Hatası: ${err.message}`);
            setStatusText('');
        }
    };

    const addFooter = (doc, pageNum) => {
        const pageHeight = doc.internal.pageSize.getHeight();
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFillColor(245, 245, 245);
        doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');

        doc.setDrawColor(22, 101, 52);
        doc.setLineWidth(0.5);
        doc.line(10, pageHeight - 15, pageWidth - 10, pageHeight - 15);

        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text('SWAMED - Akıllı Tarım Karar Destek Sistemi', 15, pageHeight - 8);
        doc.text(`Sayfa ${pageNum}`, pageWidth - 15, pageHeight - 8, { align: 'right' });
    };

    const renderChart = (data, color, name) => {
        if (!data || data.length === 0) return <p className="text-gray-400 text-sm italic text-center">No data</p>;

        const chartData = data.slice(-15).map(d => ({
            date: new Date(d.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }),
            value: d.value
        }));

        return (
            <div style={{ height: '180px', width: '100%', marginTop: '5px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                        <XAxis dataKey="date" style={{ fontSize: '10px' }} tick={{ fill: '#6b7280' }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 1]} style={{ fontSize: '10px' }} tick={{ fill: '#6b7280' }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '4px', border: '1px solid #e5e7eb', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} />
                        <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={{ r: 2, fill: color }} activeDot={{ r: 4 }} isAnimationActive={false} />
                        <Area type="monotone" dataKey="value" fill={color} fillOpacity={0.1} stroke={false} isAnimationActive={false} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        );
    };

    return (
        <div style={{ marginTop: '1rem' }}>
            <button
                onClick={handleGenerateReport}
                disabled={loading}
                style={{
                    background: loading ? '#9ca3af' : '#166534',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}
            >
                {loading ? 'Rapor Hazırlanıyor...' : '📄 Profesyonel Rapor Hazırla'}
            </button>

            {error && (
                <div style={{ marginTop: '10px', padding: '10px', background: '#fee2e2', color: '#b91c1c', borderRadius: '6px' }}>
                    ⚠️ {error}
                </div>
            )}

            {/* PREVIEW MODAL */}
            {reportData && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.7)', zIndex: 9999,
                    display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
                    overflowY: 'auto', padding: '20px'
                }}>
                    <div style={{
                        background: 'white', width: '900px', maxWidth: '95vw',
                        borderRadius: '8px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                        position: 'relative', overflow: 'hidden'
                    }}>
                        {/* Toolbar */}
                        <div style={{
                            padding: '15px 25px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            position: 'sticky', top: 0, zIndex: 50
                        }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#0f172a' }}>Rapor Önizleme</h3>
                                <div style={{ fontSize: '13px', color: '#64748b' }}>{statusText}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={() => setReportData(null)} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer' }}>Kapat</button>
                                <button onClick={generatePDF} style={{ padding: '8px 16px', borderRadius: '6px', background: '#2563eb', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>📥 PDF İndir</button>
                            </div>
                        </div>

                        {/* REPORT CONTENT */}
                        <div ref={reportRef} id="report-content" style={{ padding: '50px', background: 'white', color: '#1e293b', fontFamily: "'Segoe UI', sans-serif" }}>

                            {/* 1. Header Section */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '4px solid #166534', paddingBottom: '20px', marginBottom: '30px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                    {/* Logo Placeholder if image fails */}
                                    <div style={{ width: '60px', height: '60px', background: '#f0fdf4', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <img src={logo} alt="Logo" style={{ maxWidth: '40px', maxHeight: '40px' }} onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerText = '🌾'; e.target.parentNode.style.fontSize = '30px'; }} />
                                    </div>
                                    <div>
                                        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '800', color: '#166534', letterSpacing: '-0.5px' }}>SWAMED</h1>
                                        <div style={{ fontSize: '12px', fontWeight: '600', color: '#15803d', letterSpacing: '2px', textTransform: 'uppercase' }}>Smart Agriculture Platform</div>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0f172a' }}>FIELD ANALYSIS REPORT</div>
                                    <div style={{ fontSize: '14px', color: '#64748b', marginTop: '5px' }}>Date: {new Date().toLocaleDateString('en-GB')}</div>
                                </div>
                            </div>

                            {/* 2. Field Info Summary */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '30px', background: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <div>
                                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold' }}>Field Name</div>
                                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a' }}>{reportData.fieldInfo.name}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold' }}>Crop</div>
                                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a' }}>{reportData.fieldInfo.cropType || 'Not specified'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold' }}>Area</div>
                                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a' }}>{reportData.fieldInfo.area} ha</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold' }}>Location</div>
                                    <div style={{ fontSize: '14px', color: '#0f172a' }}>{reportData.fieldInfo.center.lat.toFixed(4)}, {reportData.fieldInfo.center.lng.toFixed(4)}</div>
                                </div>
                            </div>

                            {/* 3. Vegetation Analysis */}
                            <div style={{ marginBottom: '40px', pageBreakInside: 'avoid' }}>
                                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#166534', borderBottom: '2px solid #bbf7d0', paddingBottom: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span>🛰️</span> SATELLITE IMAGE ANALYSIS (Vegetation)
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px' }}>
                                        <div style={{ textAlign: 'center', fontWeight: 'bold', color: '#15803d', marginBottom: '10px' }}>NDVI (Health)</div>
                                        {renderChart(reportData?.vegetationData?.ndvi?.timeSeries, '#22c55e', 'NDVI')}
                                    </div>
                                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px' }}>
                                        <div style={{ textAlign: 'center', fontWeight: 'bold', color: '#0369a1', marginBottom: '10px' }}>NDMI (Moisture)</div>
                                        {renderChart(reportData?.vegetationData?.ndmi?.timeSeries, '#0ea5e9', 'NDMI')}
                                    </div>
                                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px' }}>
                                        <div style={{ textAlign: 'center', fontWeight: 'bold', color: '#b91c1c', marginBottom: '10px' }}>NDRE (Nitrogen)</div>
                                        {renderChart(reportData?.vegetationData?.ndre?.timeSeries, '#ef4444', 'NDRE')}
                                    </div>
                                </div>
                            </div>

                            {/* 4. Sensor Data Section */}
                            {reportData.sensorData && reportData.sensorData.length > 0 && (
                                <div style={{ marginBottom: '40px', pageBreakInside: 'avoid' }}>
                                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#b45309', borderBottom: '2px solid #fde68a', paddingBottom: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span>📡</span> IoT SENSOR DATA (Current Status)
                                    </h3>

                                    {/* Sensor Cards */}
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '20px' }}>
                                        {reportData.sensorData.map((sensor, idx) => {
                                            const latest = sensor.readings?.[sensor.readings.length - 1] || {};
                                            return (
                                                <div key={idx} style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '15px' }}>
                                                    <div style={{ fontWeight: 'bold', color: '#92400e', marginBottom: '10px', borderBottom: '1px solid #fde68a', paddingBottom: '5px' }}>
                                                        {sensor.sensorName || `Sensor #${idx + 1}`}
                                                    </div>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                                                        <div>🌡️ Air: <b>{latest.airTemperature || sensor.latestValue || '-'}°C</b></div>
                                                        <div>💧 Soil: <b>{latest.soilMoisture || '-'}%</b></div>
                                                        <div>💨 Humidity: <b>{latest.humidity || latest.airHumidity || '-'}%</b></div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Daily Average Table */}
                                    {reportData.sensorData[0]?.dailyAverages?.length > 0 && (
                                        <div>
                                            <h4 style={{ fontSize: '14px', fontWeight: 'bold', color: '#78350f', marginBottom: '10px' }}>Daily Sensor Averages</h4>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', border: '1px solid #fcd34d' }}>
                                                <thead>
                                                    <tr style={{ background: '#fff7ed', color: '#9a3412' }}>
                                                        <th style={{ padding: '8px', border: '1px solid #fed7aa', textAlign: 'left' }}>Date</th>
                                                        {reportData.sensorData.map((s, i) => (
                                                            <th key={i} style={{ padding: '8px', border: '1px solid #fed7aa', textAlign: 'right' }}>{s.sensorName} (Avg)</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reportData.sensorData[0].dailyAverages.slice(-7).map((dayStat, i) => (
                                                        <tr key={i} style={{ borderBottom: '1px solid #ffedd5' }}>
                                                            <td style={{ padding: '6px 8px', borderRight: '1px solid #fed7aa' }}>{new Date(dayStat.date).toLocaleDateString('en-GB')}</td>
                                                            {reportData.sensorData.map((s, j) => {
                                                                const dayData = s.dailyAverages?.find(d => d.date === dayStat.date);
                                                                return (
                                                                    <td key={j} style={{ padding: '6px 8px', textAlign: 'right', borderRight: '1px solid #fed7aa' }}>
                                                                        {dayData ? `${dayData.avg}${s.type === 'Temperature' ? '°C' : '%'}` : '-'}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* 5. Weather Section */}
                            {reportData.weatherData && reportData.weatherData.dailyStats && (
                                <div style={{ marginBottom: '40px', pageBreakInside: 'avoid' }}>
                                    <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0369a1', borderBottom: '2px solid #bae6fd', paddingBottom: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span>🌦️</span> WEATHER FORECAST (7-Day)
                                    </h3>

                                    {/* Weather Grid */}
                                    <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px', marginBottom: '15px' }}>
                                        {reportData.weatherData.dailyStats.slice(0, 7).map((day, i) => (
                                            <div key={i} style={{ flex: 1, minWidth: '80px', background: '#f0f9ff', border: '1px solid #e0f2fe', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                                                <div style={{ fontSize: '11px', color: '#64748b' }}>{new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short' })}</div>
                                                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#0c4a6e', margin: '5px 0' }}>
                                                    {day.maxTemp}°
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#0ea5e9' }}>
                                                    {day.precipitation > 0 ? `☔ ${day.precipitation}mm` : '☀️ Clear'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Daily Weather Averages Table */}
                                    <h4 style={{ fontSize: '14px', fontWeight: 'bold', color: '#075985', marginBottom: '10px' }}>Daily Weather Averages</h4>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', border: '1px solid #bae6fd' }}>
                                        <thead>
                                            <tr style={{ background: '#f0f9ff', color: '#0369a1' }}>
                                                <th style={{ padding: '8px', border: '1px solid #bae6fd', textAlign: 'left' }}>Date</th>
                                                <th style={{ padding: '8px', border: '1px solid #bae6fd', textAlign: 'right' }}>Temp (Avg)</th>
                                                <th style={{ padding: '8px', border: '1px solid #bae6fd', textAlign: 'right' }}>Precipitation</th>
                                                <th style={{ padding: '8px', border: '1px solid #bae6fd', textAlign: 'right' }}>Wind (Max)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.weatherData.dailyStats.map((day, i) => (
                                                <tr key={i} style={{ borderBottom: '1px solid #bae6fd' }}>
                                                    <td style={{ padding: '6px 8px', borderRight: '1px solid #bae6fd' }}>{new Date(day.date).toLocaleDateString('en-GB')}</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', borderRight: '1px solid #bae6fd' }}>{day.avgTemp.toFixed(1)}°C</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right', borderRight: '1px solid #bae6fd' }}>{day.precipitation}mm</td>
                                                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{day.windSpeed} km/s</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* 6. AI Analysis Section */}
                            <div style={{ marginBottom: '30px' }}>
                                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#4338ca', borderBottom: '2px solid #c7d2fe', paddingBottom: '8px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span>🤖</span> AI AGRICULTURAL ADVISOR ANALYSIS
                                </h3>
                                <div style={{ background: '#f8fafc', padding: '25px', borderRadius: '12px', border: '1px solid #e2e8f0', lineHeight: '1.8', fontSize: '14px', color: '#334155', whiteSpace: 'pre-wrap', textAlign: 'justify' }}>
                                    {(reportData.aiAnalysis || 'Analysis report could not be generated due to a technical issue. Please review the data from the charts.')
                                        .split('\n').map((line, i) => {
                                            if (line.trim().startsWith('#')) {
                                                const level = line.match(/^#+/)[0].length;
                                                const text = line.replace(/^#+\s*/, '');
                                                return <div key={i} style={{
                                                    fontSize: level === 1 ? '16px' : '15px',
                                                    fontWeight: 'bold',
                                                    color: level === 1 ? '#1e293b' : '#334155',
                                                    marginTop: '20px', marginBottom: '10px',
                                                    borderLeft: '4px solid #4338ca', paddingLeft: '10px'
                                                }}>{text}</div>;
                                            }
                                            if (line.trim().match(/^\d+\./)) {
                                                return <div key={i} style={{ fontWeight: '600', marginTop: '10px', color: '#1e293b' }}>{line}</div>;
                                            }
                                            return <p key={i} style={{ marginBottom: '10px' }}>{line}</p>;
                                        })}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportGenerator;
